<template>
  <!-- oveflow: visible dirty hack is used so tooltip can appear without being cut by drawer frame -->
  <Drawer
    :on-close="closePanel"
    :close-icon-position="closeIconPosition"
    :close-title="I18n.t('editor.modal_close')"
    container-padding-class="sm:p-6 scrollable-drawer"
  >
    <template #content>
      <VerticalScrollContentContainer>
        <div :class="drawerContentStyles">
          <div :class="effectWrapperStyles">
            <div class="flex items-center gap-2">
              <Toggle
                :name="I18n.t('editor.blur_background')"
                :model-value="props.selectedImageState.isBackgroundBlurEnabled"
                @update:modelValue="updateBlurState"
                :label="I18n.t('editor.blur_background')"
              />
            </div>
            <RangeSlider
              :value="$props.selectedImageState?.blurRadius"
              @slider-value-changed="changeBlurRadius"
              :label="I18n.t('editor.blur_amount')"
              :disabled="!props.selectedImageState.isBackgroundBlurEnabled"
              :min="0"
              :max="25"
              :step="1"
              :on-mousedown="cacheState"
              :on-touchstart="cacheState"
              :on-mouseup="applyBlur"
              :on-touchend="applyBlur"
              :inline-label="isMobile"
            />
          </div>
          <div :class="effectWrapperStyles">
            <div class="flex items-center gap-2">
              <Toggle
                v-if="shadowToggleEnabled"
                :name="I18n.t('editor.apply_shadow')"
                :model-value="props.selectedImageState.isShadowLayerVisible"
                :label="I18n.t('editor.apply_shadow')"
                @update:modelValue="updateShadowVisibility"
              />
              <span
                v-else
                class="hover-tooltip"
                :data-tippy-content="I18n.t('editor.no_shadow_tooltip')"
                data-tippy-placement="right"
              >
                <Toggle
                  :name="I18n.t('editor.apply_shadow')"
                  :model-value="false"
                  :label="I18n.t('editor.apply_shadow')"
                  :disabled="!shadowToggleEnabled"
                  @update:modelValue="updateShadowVisibility"
                />
              </span>
              <a
                href="https://www.remove.bg/help/a/enhance-your-images-with-the-ai-shadow-feature"
                class="text-sm text-typo-tertiary pointer no-underline hover:text-brand-typo flex items-center gap-1"
                target="_blank"
                >{{ I18n.t("editor.beta") }} <OpenInNewIcon style="scale: 0.8" />
              </a>
            </div>
            <RangeSlider
              :value="$props.selectedImageState?.shadowOpacity"
              @slider-value-changed="updateShadowOpacityValue"
              :label="I18n.t('editor.opacity')"
              :disabled="!props.selectedImageState.isShadowLayerVisible || !shadowToggleEnabled"
              :on-mousedown="cacheState"
              :on-touchstart="cacheState"
              :on-mouseup="applyShadow"
              :on-touchend="applyShadow"
              :inline-label="isMobile"
            />
          </div>
        </div>

        <div class="hidden sm:flex flex-row gap-2 sm:pt-3">
          <div class="hidden sm:flex flex-col gap-3 w-full">
            <HorizontalLine />

            <CanvaCtaNew
              :image="store.selectedImage"
              :persistentStore="props.persistentStore"
              :refreshAllHdLayers="props.refreshAllHdLayers"
              :stage="props.stage"
              :stageHd="props.stageHd"
              :getDataURL="props.getDataURL"
              :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
              :canvaCtaIdentifier="CanvaCTAIdentifier.ADD_FILTERS"
              :icon="FxIcon"
              :label="I18n.t('canva_cta.flows.add_filters')"
            />

            <CanvaContentImage
              :image="store.selectedImage"
              :src="effectsContentImage"
              alt="Add filters and effects to your image in Canva"
              :getDataURL="props.getDataURL"
              :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
              :canvaCtaIdentifier="CanvaCTAIdentifier.ADD_FILTERS_VISUAL"
              :stage="props.stage"
              :stageHd="props.stageHd"
              :persistentStore="props.persistentStore"
              :refreshAllHdLayers="props.refreshAllHdLayers"
            />
          </div>
        </div>
      </VerticalScrollContentContainer>

      <div class="flex sm:hidden flex-col w-full">
        <CanvaCtaNew
          :image="store.selectedImage"
          :persistentStore="props.persistentStore"
          :refreshAllHdLayers="props.refreshAllHdLayers"
          :stage="props.stage"
          :stageHd="props.stageHd"
          :getDataURL="props.getDataURL"
          :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
          :canvaCtaIdentifier="CanvaCTAIdentifier.ADD_FILTERS"
          :icon="FxIcon"
          :label="I18n.t('canva_cta.flows.add_filters')"
          class="!px-3"
        />

        <hr class="divider flex sm:hidden" />
        <p class="sm:hidden text-typo-secondary text-xs text-center mt-2 mb-1 px-3 pb-4" v-html="tos_html"></p>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import tippy from "tippy.js";

import { Toggle, Drawer, OpenInNewIcon, FxIcon } from "prism";

import RangeSlider from "../prism/range_slider.vue";
import { onMounted, ref, inject, ComputedRef, computed } from "vue";
import { useEditorStore } from "@/stores/editor_store";
import { rbgEditorApplyBlurV100, rbgEditorApplyShadowV101 } from "kaleido-event-tracker";
import { useMonitorSize } from "@/composables/monitor_size";

import effectsContentImage from "@assets/images/inline-editor/effects_panel_content.png";
import CanvaCtaNew from "@/components/canva_cta_new.vue";
import { CanvaVariant, CanvaCTAIdentifier } from "@/composables/canva_link_builder";
import { Stage } from "konva/lib/Stage";
import CanvaContentImage from "@/components/upload/canva_content_image.vue";
import { I18n as I18nType } from "i18n-js";
import HorizontalLine from "@/components/upload/editor_components/horizontal_line.vue";
import { PersistentStore } from "@/stores/persistent_store";
import { Image } from "@/modules/internal_api/image";
import { usePhotoEditor } from "@/composables/photo_editor";
import VerticalScrollContentContainer from "@/components/prism/vertical_scroll_content_container.vue";

const I18n: I18nType = inject("I18n");
const shadowToggleEnabled: ComputedRef<boolean> = inject("shadowToggle");
const { isSmallTablet } = useMonitorSize();

interface AddBackgroundProps {
  foregroundType: string;
  selectedImageState: any;
  stage: Stage;
  stageHd: Promise<Stage>;
  getDataURL: Function;
  isCanvaIntegrationEnabled: boolean;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}

const props = defineProps<AddBackgroundProps>();
const store = useEditorStore();

const tos_html = I18n.t("canva_cta.common.tos_link_html", { link: "https://www.canva.com/policies/terms-of-use/" });

const cachedState = ref<any>(null);
const nextBlurRadius = ref<number>(0);
const nextShadowOpacity = ref<number>(0);

const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

onMounted(() => {
  nextBlurRadius.value = props.selectedImageState.blurRadius;
  nextShadowOpacity.value = props.selectedImageState.shadowOpacity;
  tippy("[data-tippy-content]", { allowHTML: true, interactive: true });
});

const applyShadow = () => {
  props.selectedImageState.snapshotWithState(cachedState.value);
  props.selectedImageState.persist();

  rbgEditorApplyShadowV101({
    image_id: store.selectedImage.meta.id,
    opacity_value: props.selectedImageState.shadowOpacity,
    default_shadow: false,
  });
};

const updateShadowVisibility = (value: boolean): void => {
  props.selectedImageState.withSnapshot(() => {
    props.selectedImageState.setShadowLayerVisible(value);
  });

  const opacity = value ? props.selectedImageState.shadowOpacity : 0;
  rbgEditorApplyShadowV101({ image_id: store.selectedImage.meta.id, opacity_value: opacity, default_shadow: false });
};

const updateShadowOpacityValue = (value) => {
  nextShadowOpacity.value = parseInt(value, 10);
  props.selectedImageState.setShadowOpacity(nextShadowOpacity.value);
};

const updateBlurState = (value: boolean): void => {
  props.selectedImageState.withSnapshot(() => {
    props.selectedImageState.setBackgroundBlurEnabled(value);
  });

  const eventBlurRadius = value === true ? props.selectedImageState.blurRadius : 0;
  rbgEditorApplyBlurV100({ image_id: store.selectedImage.meta.id, blur_value: eventBlurRadius });
};

const cacheState = () => {
  const clonedState = JSON.parse(JSON.stringify(props.selectedImageState.current));
  cachedState.value = clonedState;
};

const applyBlur = () => {
  props.selectedImageState.setBlurRadius(nextBlurRadius.value);
  props.selectedImageState.snapshotWithState(cachedState.value);
  props.selectedImageState.persist();
  rbgEditorApplyBlurV100({ image_id: store.selectedImage.meta.id, blur_value: props.selectedImageState.blurRadius });
};

const emit = defineEmits<{
  (e: "closePanel"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const changeBlurRadius = (blurRadius) => {
  const parsedBlurRadius = parseInt(blurRadius, 10);
  nextBlurRadius.value = parsedBlurRadius;
};

const closeIconPosition = computed(() => {
  return isNewPhotoEditor.value && !isSmallTablet.value ? "none" : "floating";
});
const drawerContentStyles = computed(() => {
  return {
    "min-width-desktop inline-block": !isNewPhotoEditor.value,
    "!pt-4": isSmallTablet.value,
  };
});
const effectWrapperStyles = computed(() => {
  return {
    "effect-container flex flex-col gap-3 w-full pb-4": true,
    "h-24 !px-4": isSmallTablet.value,
  };
});
</script>

<style scoped lang="scss">
.min-width-desktop {
  min-width: 18rem;
  width: 100%;
}
</style>
